import { DocumentNode, useLazyQuery, useQuery } from "@apollo/client";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { Autocomplete, Box, Button, Chip, debounce, Grid, IconButton, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CITY_FILTER_AUTOCOMPLETE, COUNTRY_FILTER_AUTOCOMPLETE, SEARCH_USERS, STATE_FILTER_AUTOCOMPLETE, USER_FIELDS } from "./users.queries";
import dayjs from "dayjs";
import { DatePreset, DateRangeField } from "../reports/DateRangeField";
import { presetRange } from "../reports/reportFormat";

export interface FilterItem {
  field: string;
  type: string;
  kind: string;
  operator: string;
  value: string | boolean | number | Date | { displayName: string, name: string } | [Date, Date];
  /** Which ready range a date condition shows. The dates themselves are in `value`. */
  datePreset?: DatePreset;
}

export interface Filter {
  filters: Array<{
    field: string;
    operator: string;
    value: string
  }>;
  operator: 'OR' | 'AND';
  usersThatHaveReferred: boolean;
  usersThatHaveBeenReferred: boolean;
  usersThatHaveRecommended: boolean;
  usersThatHaveRecommendedSameBusiness: boolean;
  visitedPage: string;
  hasSharedReferralLink: boolean;
  referredByUser: string;
}

interface UserFilterProps {
  onFilter: (filter: Filter) => void;
}

type ToggleKey = 'usersThatHaveReferred' | 'usersThatHaveBeenReferred' | 'usersThatHaveRecommended' | 'usersThatHaveRecommendedSameBusiness' | 'hasSharedReferralLink';

const TOGGLES: Array<{ key: ToggleKey, label: string }> = [
  { key: 'usersThatHaveReferred', label: 'Referred someone' },
  { key: 'usersThatHaveBeenReferred', label: 'Were referred' },
  { key: 'usersThatHaveRecommended', label: 'Recommended a business' },
  { key: 'usersThatHaveRecommendedSameBusiness', label: 'Recommended the same business' },
  { key: 'hasSharedReferralLink', label: 'Shared a referral link' },
];

const NO_TOGGLES: Record<ToggleKey, boolean> = {
  usersThatHaveReferred: false,
  usersThatHaveBeenReferred: false,
  usersThatHaveRecommended: false,
  usersThatHaveRecommendedSameBusiness: false,
  hasSharedReferralLink: false,
};

const NO_USER = { displayName: '', name: '' };

/** The region fields are picked from a list of what exists, so only an exact match makes sense. */
const REGION_FIELDS = ['regionCity', 'regionState', 'regionCountry'];

const FIELD_LABELS: Record<string, string> = {
  id: 'User id',
  uid: 'Firebase uid',
  regionCity: 'City',
  regionState: 'State',
  regionStateCode: 'State code',
  regionCountry: 'Country',
  regionCountryCode: 'Country code',
  tShirtSize: 'T-shirt size',
  createdAt: 'Joined',
  latestIpAddress: 'Latest IP address',
};

const OPERATOR_LABELS: Record<string, string> = { equals: 'is', contains: 'contains', startsWith: 'starts with' };

/** A field's name as a person reads it: `isEmailVerified` is "Email verified". */
export const fieldLabel = (name: string): string => {
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  const words = name.replace(/^is(?=[A-Z])/, '').replace(/([A-Z0-9])/g, ' $1').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
};

/** True when any of it would narrow the list. */
export const isFiltering = (filter: Filter): boolean =>
  filter.filters.length > 0 || Boolean(filter.visitedPage) || Boolean(filter.referredByUser) || TOGGLES.some(({ key }) => filter[key]);

/** A condition with nothing to compare against says nothing, so it is not sent. */
const isComplete = (item: FilterItem): boolean => {
  if (item.field === '' || item.value === '') return false;
  return !(typeof item.value === 'object' && 'name' in item.value && item.value.name === '');
};

/** What a condition starts with on this field. A value left from another field's kind would be sent as nonsense. */
const startingValue = (type: string): Pick<FilterItem, 'value' | 'datePreset'> => {
  if (type === 'Boolean') return { value: true };
  if (type === 'DateTime') {
    const { from, to } = presetRange(30);
    return { value: [from, to], datePreset: '30d' };
  }
  return { value: '' };
};

/**
 * Filters a list of users. A page gets the result from `onFilter` when Apply is
 * pressed, in a shape the API takes as is, and owns the query itself.
 */
export const UserFilters: React.FC<UserFilterProps> = (props: UserFilterProps): JSX.Element => {
  const { data, loading, error } = useQuery(USER_FIELDS);
  const [filterItems, setFilterItems] = useState<Array<FilterItem>>([]);
  const [toggles, setToggles] = useState(NO_TOGGLES);
  const [visitedPage, setVisitedPage] = useState('');
  const [referredByUser, setReferredByUser] = useState<{ displayName: string, name: string }>(NO_USER);
  const [operator, setOperator] = useState<'AND' | 'OR'>('AND');
  const [applied, setApplied] = useState(false);

  const handleFilter = () => {
    const nonEmptyFilters = filterItems.filter(isComplete);
    setFilterItems(nonEmptyFilters);
    setApplied(true);
    props.onFilter({
      filters: [
        ...nonEmptyFilters.filter((item) => item.type !== 'DateTime').map((item) => ({
          field: item.field,
          operator: item.operator,
          value: `${(typeof item.value === 'object' && !(item.value instanceof Date) && !Array.isArray(item.value)) ? item.value.name : item.value}`
        })),
        ...(nonEmptyFilters.filter((item) => item.type === 'DateTime').map((item) => [
          {
            field: item.field,
            operator: 'gte',
            value: dayjs((item.value as any)[0]).startOf('day').toDate().toISOString(),
          },
          {
            field: item.field,
            operator: 'lte',
            value: dayjs((item.value as any)[1]).endOf('day').toDate().toISOString(),
          }
        ])).flat()
      ],
      operator,
      ...toggles,
      visitedPage,
      referredByUser: referredByUser.name,
    });
  };

  const clearAll = () => {
    setFilterItems([]);
    setToggles(NO_TOGGLES);
    setVisitedPage('');
    setReferredByUser(NO_USER);
    setOperator('AND');
    setApplied(false);
    props.onFilter({ filters: [], operator: 'AND', ...NO_TOGGLES, visitedPage: '', referredByUser: '' });
  };

  const applyOnEnter = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleFilter();
  };

  const updateFilterItem = (item: FilterItem, index: number) => {
    setFilterItems(filterItems.map((current, i) => (i === index ? item : current)));
  };

  const fields = useMemo(() => {
    if (loading || error) {
      return [];
    }
    return data.fetchUserFields.fields
      .filter((field: any) => !field.isReference && !['cityId', 'stateId', 'countryId'].includes(field.name))
      .sort((a: any, b: any) => fieldLabel(a.name).localeCompare(fieldLabel(b.name)));
  }, [data, error, loading]);

  const anythingSet = applied || filterItems.length > 0 || Object.values(toggles).some(Boolean) || Boolean(visitedPage) || Boolean(referredByUser.name);

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Only users who
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {TOGGLES.map(({ key, label }) => (
            <Chip
              key={key}
              label={label}
              color={toggles[key] ? 'primary' : 'default'}
              variant={toggles[key] ? 'filled' : 'outlined'}
              aria-pressed={toggles[key]}
              onClick={() => setToggles({ ...toggles, [key]: !toggles[key] })}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <TextField
          label="Visited page"
          fullWidth
          value={visitedPage}
          onChange={(event: any) => {
            setVisitedPage(event.target.value ?? '');
          }}
          onKeyDown={applyOnEnter}
        />
        <FilterAutocomplete
          query={SEARCH_USERS}
          label="Referred by"
          noOptionsText="No user"
          optionExtractor={(data) => data.searchUsers.map((user: any) => ({
            displayName: `${user.firstName} ${user.lastName} (${user.username})`,
            name: user.username,
          }))}
          value={referredByUser}
          onChange={(value) => {
            setReferredByUser(value);
          }}
        />
      </Box>

      {filterItems.length > 0 && (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {filterItems.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Users must match</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={operator}
                aria-label="Users must match"
                onChange={(_, next: 'AND' | 'OR' | null) => next && setOperator(next)}
              >
                <ToggleButton value="AND" sx={{ py: 0.25 }}>All conditions</ToggleButton>
                <ToggleButton value="OR" sx={{ py: 0.25 }}>Any condition</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          {filterItems.map((item, index) => {
            const isRegion = REGION_FIELDS.includes(item.field);
            const isText = item.type === 'String' && !isRegion;
            return (
              <Box
                key={`item-${index}-${item.field}`}
                role="group"
                aria-label={`Condition ${index + 1}`}
                sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}
              >
                <Autocomplete
                  disableClearable
                  options={fields}
                  value={fields.find((field: any) => field.name === item.field) ?? (null as any)}
                  getOptionLabel={(field: any) => fieldLabel(field.name)}
                  isOptionEqualToValue={(a: any, b: any) => a.name === b.name}
                  onChange={(_, field: any) => {
                    updateFilterItem({
                      field: field.name,
                      type: field.type,
                      kind: field.kind,
                      operator: 'equals',
                      ...startingValue(field.type),
                    }, index);
                  }}
                  renderInput={(params) => <TextField {...params} label="Field" />}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 1 240px' } }}
                />
                {isText && (
                  <TextField
                    select
                    label="Match"
                    value={item.operator}
                    onChange={(event: any) => {
                      updateFilterItem({
                        ...item,
                        operator: event.target.value,
                      }, index);
                    }}
                    sx={{ flex: '0 1 140px' }}
                  >
                    {Object.keys(OPERATOR_LABELS).map((value) => (
                      <MenuItem key={value} value={value}>{OPERATOR_LABELS[value]}</MenuItem>
                    ))}
                  </TextField>
                )}
                <Box sx={{ flex: '1 1 180px', minWidth: 0, '& > .MuiFormControl-root, & > .MuiAutocomplete-root': { width: '100%' } }}>
                  {item.field === '' && <TextField label="Value" disabled />}
                  {item.type === 'Boolean' && (
                    <TextField
                      select
                      label="Value"
                      value={String(item.value === true)}
                      onChange={(event: any) => {
                        updateFilterItem({
                          ...item,
                          value: event.target.value === 'true',
                        }, index);
                      }}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  )}
                  {(isText || item.kind === 'enum') && (
                    <TextField
                      label="Value"
                      value={item.value}
                      onChange={(event: any) => {
                        updateFilterItem({
                          ...item,
                          value: event.target.value,
                        }, index);
                      }}
                      onKeyDown={applyOnEnter}
                    />
                  )}
                  {item.type === 'Int' && (
                    <TextField
                      label="Value"
                      type="number"
                      value={item.value}
                      onChange={(event: any) => {
                        updateFilterItem({
                          ...item,
                          value: event.target.value,
                        }, index);
                      }}
                      onKeyDown={applyOnEnter}
                    />
                  )}
                  {item.type === 'DateTime' && (
                    <DateRangeField
                      fullWidth
                      value={{
                        preset: item.datePreset ?? 'custom',
                        from: (item.value as any)[0],
                        to: (item.value as any)[1],
                      }}
                      onChange={(next) => {
                        updateFilterItem({
                          ...item,
                          value: [next.from as Date, next.to as Date],
                          datePreset: next.preset,
                        }, index);
                      }}
                    />
                  )}
                  {item.field === 'regionCountry' && (<
                    FilterAutocomplete
                    query={COUNTRY_FILTER_AUTOCOMPLETE}
                    label="Country"
                    noOptionsText="No country"
                    value={item.value === '' ? NO_USER : item.value as { displayName: string, name: string }}
                    optionExtractor={(data) => data.getCountriesAutocomplete.map((country: any) => ({
                      displayName: country.regionCountry,
                      name: country.regionCountry,
                    }))}
                    onChange={(value) => {
                      updateFilterItem({
                        ...item,
                        value: value,
                      }, index);
                    }}
                  />
                  )}
                  {item.field === 'regionState' && (<
                    FilterAutocomplete
                    query={STATE_FILTER_AUTOCOMPLETE}
                    label="State"
                    noOptionsText="No state"
                    value={item.value === '' ? NO_USER : item.value as { displayName: string, name: string }}
                    optionExtractor={(data) => data.getStatesAutocomplete.map((state: any) => ({
                      name: state.regionState,
                      displayName: `${state.regionState}, ${state.regionCountry}`,
                    }))}
                    onChange={(value) => {
                      updateFilterItem({
                        ...item,
                        value: value,
                      }, index);
                    }}
                  />
                  )}
                  {item.field === 'regionCity' && (<
                    FilterAutocomplete
                    query={CITY_FILTER_AUTOCOMPLETE}
                    label="City"
                    noOptionsText="No city"
                    value={item.value === '' ? NO_USER : item.value as { displayName: string, name: string }}
                    optionExtractor={(data) => data.getCitiesAutocomplete.map((city: any) => ({
                      name: city.regionCity,
                      displayName: `${city.regionCity}, ${city.regionState}, ${city.regionCountry}`,
                    }))}
                    onChange={(value) => {
                      updateFilterItem({
                        ...item,
                        value: value,
                      }, index);
                    }}
                  />
                  )}
                </Box>
                <Tooltip title="Remove this condition">
                  <IconButton
                    size="small"
                    aria-label={`Remove condition ${index + 1}`}
                    onClick={() => setFilterItems(filterItems.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error">
          Could not load the fields to filter on: {error.message}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Button
          startIcon={<AddRounded />}
          disabled={loading || Boolean(error)}
          onClick={() => setFilterItems([...filterItems, { field: '', operator: 'equals', value: '', type: '', kind: '' }])}
          sx={{ ml: -0.75 }}
        >
          Add condition
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {anythingSet && <Button onClick={clearAll}>Clear all</Button>}
          <Button variant="contained" onClick={handleFilter}>Apply filters</Button>
        </Box>
      </Box>
    </Box>
  );
};


export interface FilterAutocompleteProps {
  value: { displayName: string, name: string }
  onChange: (value: { displayName: string, name: string }) => void,
  query: DocumentNode,
  noOptionsText: string,
  label: string,
  optionExtractor: (data: any) => Array<any>;
}

export const FilterAutocomplete: React.FC<FilterAutocompleteProps> = (props: FilterAutocompleteProps): JSX.Element => {
  const [inputValue, setInputValue] = useState('');
  const [getOptions, { data, loading }] = useLazyQuery(props.query);
  const [options, setOptions] = useState<any>([]);

  const fetch = useMemo(() => debounce((filter: string) => {
    getOptions({
      variables: {
        filter: !!filter ? filter : null,
      }
    });
  }, 400), [getOptions]);

  useEffect(() => {
    fetch(inputValue);
  }, [fetch, inputValue]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (data) {
      setOptions(props.optionExtractor(data));
    }
  }, [data, loading, props, props.optionExtractor]);

  return (
    <Autocomplete
      options={options}
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      filterSelectedOptions
      noOptionsText={props.noOptionsText}
      value={props.value}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event: any, newValue) => {
        props.onChange(newValue ?? { name: '', displayName: '' });
      }}
      getOptionLabel={(option: any) => option.displayName}
      renderInput={(params) => (
        <TextField {...params} label={props.label} fullWidth />
      )}
      renderOption={(props: any, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: 'center' }}>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                <Box>
                  {option.displayName}
                </Box>

              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}